import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { store } from "../../../store";
import { toast } from "react-toastify";
import { AxiosResponse } from "axios";
import API from "Utils/api";
import { closeAnyModal, getModalData } from "Slices/modals";
import { getStopsByCurrentSemanticMap } from "Slices/stops";
import { getCurrentFleetId } from "Slices/missionPlanner";
import { getCurrentSemanticMapId } from "Slices/appState";
import {
  fetchDeployMissionPlan,
  fetchPatchMissionPlan,
  fetchPostMissionPlan,
  updateMissionPlan,
  setMissionVersions,
} from "Slices/missionPlans";
import { createVersion } from "Slices/missions";
import { fetchGetFleets } from "Slices/fleets";
import { refreshMissionPlanFields } from "Slices/missionsPage";
import { getVehiclesByCurrentSemanticMap } from "Slices/vehicles";
import {
  ButtonTextContainer,
  CancelButtonText,
  CancelMissionButton,
  CloseButtonIcon,
  CloseButtonLabel,
  CloseModalButton,
  CreateButtonText,
  CreateMissionButton,
  FooterButtonsContainer,
  HeaderLeftSection,
  HeaderRightSection,
  MissionFormContainer,
  MissionModalBody,
  MissionModalFooter,
  MissionModalHeader,
  MissionModalSidebar,
  MissionModalTitle,
  ModalOverlay,
  MissionNameInputContainer,
  MissionNameLabel,
  MissionNameInputWrapper,
  MissionNameInputContent,
  VehicleTypeSelect,
} from "./styles";
import {
  MissionForm,
  MissionStops,
  ValidationError,
  VersionSelector,
} from "./components";
import { TriggerType } from "./types";
import { useMissionValidation } from "./hooks";

import { ReactComponent as CloseIconSvg } from "./assets/close.svg";
import { getVdaActionDefinitions } from "Slices/vda_action_definitions";
import { getVehicleTypes } from "Slices/vehicleTypes";

import {
  addMissionStop,
  addVdaActionToStop,
  buildMissionPayload,
  areStopConfigurationsEqual,
  countUnhitchEvents,
  getAvailableTriggerTypes,
  hasExistingTrigger,
  removeMissionStop,
  removeVdaActionFromStop,
  removeDoubleConfirmationFromStop,
  updateStopWithAction,
  updateStopWithDuration,
  updateStopWithHitchType,
  updateStopWithStation,
  updateStopWithTriggerType,
  validateStationSelections,
  countTotalTriggerTypesForStop,
  reorderMissionStops,
  reorderVdaActions,
  TRIGGER_TYPE_LIMIT_EXCEEDED_MESSAGE,
  DOUBLE_CONFIRMATION_EXCLUSIVITY_MESSAGE,
} from "./utils";
import { MissionDeploy } from "Components/common/MissionDeploy";
import { MissionDeployOption } from "Components/Modals/MissionFormModal/MissionDeployOption";
import { CONFIRMATION_TYPES, TRIGGER_TYPES } from "Components/common/constants";

interface AddMissionModalProps {
  isEditMode?: boolean;
  editData?: EditMissionData;
}

interface EditMissionData {
  id: string;
  master_mission_id?: string;
  name: string;
  timeStamp?: string | null;
  stops: MissionStop[];
}

// ============================================================================
// COMPONENT
// ============================================================================

export const AddMissionModal = memo(
  ({ isEditMode = false, editData }: AddMissionModalProps) => {
    const dispatch = useDispatch<typeof store.dispatch>();
    const stops = useSelector(getStopsByCurrentSemanticMap);
    const currentFleetId = useSelector(getCurrentFleetId);
    const semanticMapId = useSelector(getCurrentSemanticMapId);
    const vehiclesByCurrentSemanticMap = useSelector(
      getVehiclesByCurrentSemanticMap
    );
    const vehicleTypes = useSelector(getVehicleTypes);
    const modalData = useSelector(getModalData);

    //State
    const [missionName, setMissionName] = useState("");
    const [missionStops, setMissionStops] = useState<MissionStop[]>([]);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const vdaActionDefinitions = useSelector(getVdaActionDefinitions);
    const validationErrorsRef = useRef<HTMLDivElement>(null);
    const stepsListBottomRef = useRef<HTMLDivElement>(null);
    const prevCountsRef = useRef({ stops: 0, actions: 0 });
    const isInitializedRef = useRef(!isEditMode);
    const [isDeployEnabled, setIsDeployEnabled] = useState(false);
    const [cycles, setCycles] = useState(1);
    const [resetTrigger, setResetTrigger] = useState(0);
    const [selectedVehicleForDeploy, setSelectedVehicleForDeploy] = useState<{
      id: string;
      isFleet: boolean;
      cycles: number;
    } | null>(null);
    const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
      null
    );
    const [originalMissionName, setOriginalMissionName] = useState("");
    const [originalMissionStops, setOriginalMissionStops] = useState<
      MissionStop[]
    >([]);
    const [masterMissionId, setMasterMissionId] = useState<string | null>(null);
    const [selectedVehicleType, setSelectedVehicleType] = useState<
      string | null
    >(null);
    const [originalVehicleType, setOriginalVehicleType] = useState<
      string | null
    >(null);
    const [isSaving, setIsSaving] = useState(false);

    // Constants
    const PREVIOUS_MODE = "Continue";
    const submitButtonText = isEditMode ? "Save" : "Create Mission";
    const modalTitle = isEditMode ? "Edit Mission" : "Create a new mission";

    // Check if there's more than one unhitch event
    const isShowUnhitchWarning = useMemo(() => {
      return countUnhitchEvents(missionStops) > 1;
    }, [missionStops]);

    const availableTriggerTypes = useMemo(() => getAvailableTriggerTypes(), []);

    const totalActionCount = useMemo(
      () =>
        missionStops.reduce(
          (sum, s) => sum + (s.missionStopVdaActions?.length ?? 0),
          0
        ),
      [missionStops]
    );

    // Check if changes have been made (only relevant in edit mode)
    const hasChanges = useMemo(() => {
      if (!isEditMode) return true; // Always allow saving in create mode

      // Compare vehicle type
      if (selectedVehicleType !== originalVehicleType) return true;

      // Compare mission name
      if (missionName !== originalMissionName) return true;

      // Compare mission stops
      if (missionStops.length !== originalMissionStops.length) return true;

      // Deep compare mission stops
      for (let i = 0; i < missionStops.length; i++) {
        const currentStop = missionStops[i];
        const originalStop = originalMissionStops[i];

        if (!originalStop) return true;

        // Compare basic properties
        if (
          currentStop.name !== originalStop.name ||
          currentStop.duration !== originalStop.duration ||
          currentStop.latitude !== originalStop.latitude ||
          currentStop.longitude !== originalStop.longitude ||
          currentStop.hitchType !== originalStop.hitchType ||
          currentStop.confirmationType !== originalStop.confirmationType ||
          currentStop.confirmationButtonsRequired !==
            originalStop.confirmationButtonsRequired ||
          currentStop.stopPk !== originalStop.stopPk ||
          currentStop.order !== originalStop.order
        ) {
          return true;
        }

        // Compare missionStopVdaActions
        const currentActions = currentStop.missionStopVdaActions || [];
        const originalActions = originalStop.missionStopVdaActions || [];

        if (currentActions.length !== originalActions.length) return true;

        for (let j = 0; j < currentActions.length; j++) {
          const currentAction = currentActions[j];
          const originalAction = originalActions[j];

          if (!originalAction) return true;

          if (
            currentAction.actionType !== originalAction.actionType ||
            currentAction.actionDescription !==
              originalAction.actionDescription ||
            currentAction.blockingType !== originalAction.blockingType ||
            currentAction.order !== originalAction.order ||
            JSON.stringify(currentAction.actionParameters) !==
              JSON.stringify(originalAction.actionParameters)
          ) {
            return true;
          }
        }
      }

      return false;
    }, [
      isEditMode,
      missionName,
      originalMissionName,
      missionStops,
      originalMissionStops,
      selectedVehicleType,
      originalVehicleType,
    ]);

    // Filter vehicles by current fleet
    const currentVehicles = useMemo(
      () =>
        vehiclesByCurrentSemanticMap.filter(
          (vehicle) => vehicle.fleetId === currentFleetId
        ),
      [vehiclesByCurrentSemanticMap, currentFleetId]
    );

    // Validation hook
    const { validateMission } = useMissionValidation({
      missionName,
      missionStops,
      isShowUnhitchWarning,
      availableStops: stops,
    });

    // ============================================================================
    // EFFECTS
    // ============================================================================

    useLayoutEffect(() => {
      if (currentFleetId) return;

      toast.error("Please specify particular fleet before create new mission!");
      handleCloseModal();
    }, [currentFleetId]);

    useEffect(() => {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = "hidden";

      return () => {
        document.body.style.overflow = originalStyle;
      };
    }, []);

    // Scroll to validation errors when they appear
    useEffect(() => {
      if (validationErrors.length > 0 && validationErrorsRef.current) {
        validationErrorsRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
    }, [validationErrors]);

    // Auto-scroll to bottom of stops list when a new stop or action is added
    useEffect(() => {
      if (!isInitializedRef.current) {
        prevCountsRef.current = {
          stops: missionStops.length,
          actions: totalActionCount,
        };
        return;
      }
      if (
        missionStops.length > prevCountsRef.current.stops ||
        totalActionCount > prevCountsRef.current.actions
      ) {
        stepsListBottomRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
      prevCountsRef.current = {
        stops: missionStops.length,
        actions: totalActionCount,
      };
    }, [missionStops.length, totalActionCount]);

    // When the vehicle type changes, clear any previously selected deploy vehicle so
    // the deploy target cannot mismatch the new type constraint.
    useEffect(() => {
      setSelectedVehicleForDeploy(null);
      setResetTrigger((prev) => prev + 1);
    }, [selectedVehicleType]);

    // Initialize with edit data if in edit mode
    useEffect(() => {
      if (isEditMode) {
        // Use modal data if available, otherwise use editData prop
        const editMissionData = modalData || editData;

        if (editMissionData && typeof editMissionData === "object") {
          // Extract master_mission_id if available, fallback to mission id for pre-versioning missions
          if (editMissionData.master_mission_id) {
            setMasterMissionId(String(editMissionData.master_mission_id));
          } else if (editMissionData.id) {
            // Fallback: For missions created before versioning, use the mission's own ID as master_mission_id
            // This provides backward compatibility until backend data migration (INFR-13205) is complete
            setMasterMissionId(String(editMissionData.id));
          }
          if (typeof editMissionData.name === "string") {
            setMissionName(editMissionData.name);
            setOriginalMissionName(editMissionData.name);
          }
          // Load vehicle_type constraint if present
          if ("vehicle_type" in editMissionData) {
            const vt = (editMissionData as any).vehicle_type || null;
            setSelectedVehicleType(vt);
            setOriginalVehicleType(vt);
          }
          if (Array.isArray(editMissionData.stops)) {
            // Convert backend snake_case data to camelCase
            const processedStops = editMissionData.stops.map((stop: any) => {
              // Convert stop properties from snake_case to camelCase
              const convertedStop = {
                id: stop.id,
                name: stop.name,
                duration: stop.duration,
                latitude: stop.latitude,
                longitude: stop.longitude,
                hitchType: stop.hitch_type,
                confirmationType: stop.confirmation_type,
                confirmationButtonsRequired: stop.confirmation_buttons_required,
                stopPk: stop.stop_pk,
                order: stop.order,
                deletedAt: stop.deletedAt,
                // Convert VDA actions from snake_case to camelCase
                missionStopVdaActions: Array.isArray(
                  stop.mission_stop_vda_actions
                )
                  ? stop.mission_stop_vda_actions.map((action: any) => ({
                      actionId: action.action_id,
                      actionType: action.action_type,
                      actionDescription: action.action_description,
                      blockingType: action.blocking_type,
                      actionParameters: Array.isArray(action.action_parameters)
                        ? action.action_parameters.map((param: any) => ({
                            triggerType: param.trigger_type,
                            duration: param.duration,
                          }))
                        : [],
                      missionId: action.mission_id,
                      stopId: action.stop_id,
                      queuedMissionStopId: action.queued_mission_stop,
                      mapMissionStopId: action.map_mission_stop,
                      order: action.order,
                      metadata: action.metadata,
                      actionStatus: action.action_status,
                    }))
                  : [],
              };

              return convertedStop;
            });

            setMissionStops(processedStops);
            setOriginalMissionStops(processedStops);
            isInitializedRef.current = true;
          }
        }
      }
    }, [isEditMode, editData, modalData]);

    // ============================================================================
    // EVENT HANDLERS
    // ============================================================================

    const handleCloseModal = useCallback(() => {
      dispatch(closeAnyModal());
    }, [dispatch]);

    const handleAddStep = useCallback(() => {
      setMissionStops((prev) => addMissionStop(prev));
    }, []);

    const handleValidationError = useCallback((error: string) => {
      setValidationErrors((prev) => {
        if (prev.includes(error)) {
          return prev;
        }
        return [...prev, error];
      });
      toast.error(error);
    }, []);

    const clearValidationError = useCallback((errorToRemove: string) => {
      setValidationErrors((prev) =>
        prev.filter((error) => error !== errorToRemove)
      );
    }, []);

    const handleAddAction = useCallback(
      (stepIndex: number) => {
        if (!vdaActionDefinitions || vdaActionDefinitions.length === 0) {
          console.warn("No VDA action definitions available");
          toast.error("No actions available. Please try again.");
          return;
        }

        const currentStop = missionStops[stepIndex];

        // Count total trigger types for this stop (including duplicates)
        const totalTriggerTypesCount =
          countTotalTriggerTypesForStop(currentStop);

        // Check if we've reached the maximum number of trigger types
        if (totalTriggerTypesCount >= 3) {
          toast.error(
            "Cannot add more actions. Maximum of 3 trigger types allowed per stop."
          );
          return;
        }

        // Check if the current stop has double confirmation
        if (
          currentStop?.confirmationType ===
          CONFIRMATION_TYPES.CLOUD_HMI_RELEASE_CONFIRMATION
        ) {
          toast.error(
            "Cannot add more actions when Double confirmation is selected. Double confirmation must be the only action for this stop."
          );
          return;
        }

        const defaultAction = vdaActionDefinitions[0];
        const defaultActionType = defaultAction?.actionType || "";
        const defaultActionDescription = defaultAction?.description || "";

        setMissionStops((prev) =>
          addVdaActionToStop(
            prev,
            stepIndex,
            defaultActionType,
            defaultActionDescription
          )
        );
      },
      [vdaActionDefinitions, missionStops]
    );

    const handleStationSelect = useCallback(
      (station: any, stepIndex: number) => {
        setMissionStops((prev) =>
          updateStopWithStation(prev, stepIndex, station)
        );
      },
      []
    );

    const handleActionSelect = useCallback(
      (action: VdaActionDefinition, stepIndex: number, actionIndex: number) => {
        setMissionStops((prev) =>
          updateStopWithAction(prev, stepIndex, actionIndex, action)
        );
      },
      []
    );

    const handleTriggerTypeSelect = useCallback(
      (triggerType: TriggerType, stepIndex: number, actionIndex: number) => {
        const currentStop = missionStops[stepIndex];

        // Count total trigger types for this stop (including duplicates)
        const totalTriggerTypesCount =
          countTotalTriggerTypesForStop(currentStop);

        // When EDITING: the action at actionIndex already has a trigger type - we're replacing, not adding.
        // When ADDING: block if we'd exceed 3.
        if (
          !hasExistingTrigger(currentStop, actionIndex) &&
          totalTriggerTypesCount >= 3
        ) {
          toast.warning(TRIGGER_TYPE_LIMIT_EXCEEDED_MESSAGE);
          return;
        }

        // Check for other trigger types (HMI Button or Duration) - exclude the current action being edited
        const hasOtherTriggerTypes = currentStop?.missionStopVdaActions.some(
          (action, idx) => {
            // Skip the action that is being edited
            if (idx === actionIndex) {
              return false;
            }

            if (action.actionParameters && action.actionParameters.length > 0) {
              const actionTriggerType = action.actionParameters[0].triggerType;
              return (
                actionTriggerType === TRIGGER_TYPES.HMI_BUTTON ||
                actionTriggerType === TRIGGER_TYPES.DURATION
              );
            }
            return false;
          }
        );

        // Show warning for double confirmation when other trigger types exist
        if (
          triggerType.key === TRIGGER_TYPES.DOUBLE_CONFIRMATION &&
          hasOtherTriggerTypes
        ) {
          toast.warning(DOUBLE_CONFIRMATION_EXCLUSIVITY_MESSAGE);
          return;
        }

        setMissionStops((prev) =>
          updateStopWithTriggerType(prev, stepIndex, actionIndex, triggerType)
        );
      },
      [missionStops]
    );

    const handleDurationChange = useCallback(
      (duration: string, stepIndex: number, actionIndex: number) => {
        setMissionStops((prev) =>
          updateStopWithDuration(prev, stepIndex, actionIndex, duration)
        );
      },
      []
    );

    const handleRemoveAction = useCallback(
      (stepIndex: number, actionIndex: number) => {
        // Check if this is a double confirmation removal (actionIndex = -1)
        if (actionIndex === -1) {
          setMissionStops((prev) =>
            removeDoubleConfirmationFromStop(prev, stepIndex)
          );
        } else {
          setMissionStops((prev) =>
            removeVdaActionFromStop(prev, stepIndex, actionIndex)
          );
        }
      },
      []
    );

    const handleRemoveStep = useCallback((stepIndex: number) => {
      setMissionStops((prev) => removeMissionStop(prev, stepIndex));
    }, []);

    const handleUnhitchChange = useCallback(
      (stepIndex: number, isUnhitch: boolean) => {
        setMissionStops((prev) =>
          updateStopWithHitchType(prev, stepIndex, isUnhitch)
        );
      },
      []
    );

    const handleReorderSteps = useCallback(
      (startIndex: number, endIndex: number) => {
        setMissionStops((prev) =>
          reorderMissionStops(prev, startIndex, endIndex)
        );
      },
      []
    );

    const handleReorderActions = useCallback(
      (
        startStopIndex: number,
        endStopIndex: number,
        startIndex: number,
        endIndex: number
      ) => {
        setMissionStops((prev) =>
          reorderVdaActions(
            prev,
            startStopIndex,
            endStopIndex,
            startIndex,
            endIndex
          )
        );
      },
      []
    );

    const saveAndDeployMission = async (
      pickedId: string,
      selectedLoopsValue: number,
      isFleet = false,
      addedMissionData: MissionAsset,
      versionId?: string
    ) => {
      const response = await dispatch(
        fetchDeployMissionPlan({
          ...addedMissionData,
          ...(isFleet ? { fleet: pickedId } : { vehicle: pickedId }),
          loops: selectedLoopsValue,
          version: versionId,
        })
      );
      if (fetchDeployMissionPlan.fulfilled.match(response)) {
        dispatch(
          fetchPatchMissionPlan({
            id: addedMissionData.id,
            data: { ...addedMissionData, timeStamp: new Date().toISOString() },
            hideToast: true,
          })
        );
      }
    };

    const handleCreateMission = async () => {
      // Check if semanticMapId is available
      if (!semanticMapId) {
        console.error("Semantic Map ID is required to create a mission");
        toast.error("Semantic Map ID is required to create a mission");
        return;
      }

      // Validate fields
      const errors = validateMission();
      if (errors.length > 0) {
        setValidationErrors(errors);
        toast.error(
          "Please fix the validation errors before creating the mission"
        );
        return;
      }

      // Additional validation: check if all stations have valid stop_pk
      const stationErrors = validateStationSelections(missionStops);
      if (stationErrors.length > 0) {
        setValidationErrors(stationErrors);
        toast.error(stationErrors[0]);
        return;
      }

      if (isDeployEnabled && !selectedVehicleForDeploy) {
        const deployErrorMessage =
          "Please select a vehicle to deploy the mission after creation";
        toast.error(deployErrorMessage);
        setValidationErrors([deployErrorMessage]);
        return;
      }

      // Clear validation errors
      setValidationErrors([]);

      // Build and submit the mission (only create, no deploy)
      try {
        const payload = {
          ...buildMissionPayload(missionName, missionStops, semanticMapId),
          vehicle_type: selectedVehicleType || null,
        };
        const response = await dispatch(fetchPostMissionPlan(payload));
        if (fetchPostMissionPlan.fulfilled.match(response)) {
          const newMission = response.payload;
          // If deploy is enabled, we need to handle it differently
          if (isDeployEnabled && selectedVehicleForDeploy) {
            // If vehicle is selected, create and deploy the mission
            await saveAndDeployMission(
              selectedVehicleForDeploy.id,
              selectedVehicleForDeploy.cycles,
              selectedVehicleForDeploy.isFleet,
              newMission,
              selectedVersionId || undefined
            );
          }
          handleCloseModal();
        }
      } catch (error) {
        console.error("Failed to create mission:", error);
        toast.error("Failed to create mission. Please try again.");
      }
    };

    const handleDeployToggle = useCallback((enabled: boolean) => {
      setIsDeployEnabled(enabled);

      // Reset fields when deploy is disabled
      if (!enabled) {
        setCycles(1);
        setResetTrigger((prev) => prev + 1);
        setSelectedVehicleForDeploy(null);
      }
    }, []);

    const handleCyclesChange = useCallback((newCycles: number) => {
      setCycles(newCycles);
    }, []);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleUpdateMission = useCallback(() => {
      // Check if semanticMapId is available
      if (!semanticMapId) {
        console.error("Semantic Map ID is required to update a mission");
        return;
      }

      // Get the mission ID from modal data
      const editMissionData = modalData || editData;
      const missionId = (editMissionData as EditMissionData)?.id;
      if (!missionId) {
        console.error("Mission ID is required to update a mission");
        toast.error("Mission ID not found. Please try again.");
        return;
      }

      // Validate fields
      const errors = validateMission();
      if (errors.length > 0) {
        setValidationErrors(errors);
        toast.error(
          "Please fix the validation errors before updating the mission"
        );
        return;
      }

      // Clear validation errors
      setValidationErrors([]);

      // Build the final payload for update using buildMissionPayload to process stops
      const basePayload = buildMissionPayload(
        missionName || `Updated ${new Date().toLocaleDateString()}`,
        missionStops,
        semanticMapId,
        editMissionData?.timeStamp // Pass the original timeStamp
      );

      const payloadData: MissionAsset = {
        id: missionId,
        timeStamp: basePayload.timeStamp, // Use the timeStamp from basePayload
        previous: PREVIOUS_MODE,
        name: basePayload.name,
        stops: basePayload.stops,
        semanticMap: basePayload.semanticMap,
        updatedAt: new Date().toISOString(),
        vehicle_type: selectedVehicleType || null,
      };

      try {
        // Update the mission using Redux action
        dispatch(
          fetchPatchMissionPlan({
            id: missionId,
            data: payloadData,
            hideToast: false,
          })
        );

        // Close modal after successful update
        handleCloseModal();
      } catch (error) {
        console.error("Failed to update mission:", error);
        toast.error("Failed to update mission. Please try again.");
      }
    }, [
      missionName,
      missionStops,
      semanticMapId,
      modalData,
      editData,
      handleCloseModal,
      validateMission,
      dispatch,
      selectedVehicleType,
    ]);

    const handleCreateVersion = useCallback(async () => {
      // Check if master mission ID is available
      if (!masterMissionId) {
        console.error("Master Mission ID is required to create a version", {
          masterMissionId,
          modalData,
          editData,
        });
        toast.error(
          "Unable to create version: Mission data is incomplete. Please close and reopen the mission editor."
        );
        return;
      }

      // Check if semanticMapId is available
      if (!semanticMapId) {
        console.error("Semantic Map ID is required to create a version");
        toast.error("Semantic Map ID is required");
        return;
      }

      // Validate fields
      const errors = validateMission();
      if (errors.length > 0) {
        setValidationErrors(errors);
        toast.error("Please fix the validation errors before saving");
        return;
      }

      // Clear validation errors
      setValidationErrors([]);

      // Build the version payload
      const basePayload = buildMissionPayload(
        missionName || `Updated ${new Date().toLocaleDateString()}`,
        missionStops,
        semanticMapId,
        null // Don't preserve old timestamp for new version
      );

      // Create version data (matches Version type from types.d.ts)
      const versionData: Partial<Version> = {
        name: basePayload.name,
        short_name: null,
        previous: PREVIOUS_MODE,
        stops: basePayload.stops,
        semantic_map: semanticMapId,
        loops: null,
        is_uber: false,
        vehicle_type: selectedVehicleType || null,
      };

      // Get the current mission ID (not master_mission_id) for the API call
      const editMissionData = modalData || editData;
      const currentMissionId = (editMissionData as EditMissionData)?.id;

      if (!currentMissionId) {
        toast.error("Cannot create version: Mission ID not found");
        return;
      }

      setIsSaving(true);

      // Cross-version duplicate detection: compare proposed stops against ALL existing versions
      try {
        // Fetch all versions, following pagination
        let existingVersions: Array<{ id: string; versionNumber: number }> = [];
        let versionsUrl:
          | string
          | null = `/missions/${currentMissionId}/versions/`;
        while (versionsUrl) {
          const resp: AxiosResponse = await API.get(versionsUrl);
          const page: Array<{ id: string; versionNumber: number }> =
            resp.data.results ?? resp.data;
          existingVersions = existingVersions.concat(page);
          versionsUrl = resp.data.next ?? null;
        }

        // Sequential fetch with early break to avoid N+1 fan-out
        // Skip the current version to avoid false-positive when only non-stop
        // fields (e.g. mission name, vehicle type) have changed.
        for (const version of existingVersions) {
          if (version.id === currentMissionId) continue;
          const detail = await API.get(`/missions/${version.id}/`);
          const existingStops: MissionStop[] = detail.data.stops || [];

          if (areStopConfigurationsEqual(missionStops, existingStops)) {
            toast.error(
              `This mission configuration is identical to existing version ${version.versionNumber}. Please make changes before saving a new version.`
            );
            setIsSaving(false);
            return;
          }
        }
      } catch (error) {
        console.warn(
          "[DEV-1259] Failed to fetch existing versions for dedup check:",
          error
        );
        // Graceful degradation: proceed with version creation.
        // The backend also enforces duplicate detection, so this is a
        // secondary check for better UX.
      }

      try {
        // Create new version using Redux action
        // Use the current mission ID (not master_mission_id) as the API expects /missions/{mission_id}/versions/
        const response = await dispatch(
          createVersion({
            masterId: currentMissionId,
            versionData: versionData,
          })
        );

        if (createVersion.fulfilled.match(response)) {
          const newVersion = response.payload.version;

          // Only refetch for regular missions, not uber missions
          // Uber missions are excluded from /missions/{id}/ endpoint, causing 404 errors
          // Note: This is defensive - no current UI path triggers version creation for uber missions
          // See INFR-13279 for details
          if (!newVersion.is_uber) {
            // Refetch the newly created version to get fresh timestamp and versionNumber
            const latestMission = await API.get(`missions/${newVersion.id}/`);
            dispatch(updateMissionPlan(latestMission.data));

            // Update the QueuedMission in missionsPage so the MissionDetailsModal
            // shows fresh name/stops without requiring a page refresh.
            dispatch(
              refreshMissionPlanFields({
                prevMissionId: currentMissionId,
                name: latestMission.data.name,
                stops: latestMission.data.stops,
              })
            );

            // Update the missionPlans.versions cache so the VersionSelector
            // badge shows the correct version number without a page refresh.
            if (latestMission.data.masterMissionId) {
              try {
                const versionsResp = await API.get(
                  `missions/${latestMission.data.id}/versions/`
                );
                dispatch(
                  setMissionVersions({
                    masterMissionId: latestMission.data.masterMissionId,
                    versions: versionsResp.data.results ?? versionsResp.data,
                  })
                );
              } catch (e) {
                console.warn("[DEV-1258] Failed to refresh version cache:", e);
              }

              // Refresh fleet data so fleet modals show correct mission references
              dispatch(fetchGetFleets());
            }

            toast.success(
              `Version ${latestMission.data.versionNumber} created successfully`
            );
          } else {
            // For uber missions, use the version data from the create response
            dispatch(updateMissionPlan(newVersion));
            toast.success(
              `Version ${newVersion.versionNumber} created successfully`
            );
          }

          // Close modal after successful creation
          handleCloseModal();
        } else {
          toast.error("Failed to create version");
        }
      } catch (error) {
        console.error("Failed to create version:", error);
        toast.error("Failed to create version. Please try again.");
      } finally {
        setIsSaving(false);
      }
    }, [
      masterMissionId,
      semanticMapId,
      missionName,
      missionStops,
      validateMission,
      handleCloseModal,
      dispatch,
      modalData,
      editData,
      selectedVehicleType,
    ]);

    const handleSubmit = isEditMode ? handleCreateVersion : handleCreateMission;

    // ============================================================================
    // RENDER
    // ============================================================================

    return (
      <ModalOverlay onClick={handleCloseModal}>
        <MissionModalSidebar onClick={(e) => e.stopPropagation()}>
          <MissionModalHeader>
            <HeaderLeftSection>
              <MissionModalTitle>{modalTitle}</MissionModalTitle>
            </HeaderLeftSection>
            <HeaderRightSection>
              <CloseModalButton onClick={handleCloseModal}>
                <CloseButtonLabel>Close</CloseButtonLabel>
                <CloseButtonIcon>
                  <CloseIconSvg />
                </CloseButtonIcon>
              </CloseModalButton>
            </HeaderRightSection>
          </MissionModalHeader>

          <MissionModalBody>
            {/* Vehicle Type Selector */}
            <MissionNameInputContainer>
              <MissionNameLabel>Vehicle Type</MissionNameLabel>
              <MissionNameInputWrapper>
                <MissionNameInputContent>
                  <VehicleTypeSelect
                    value={selectedVehicleType || ""}
                    onChange={(e) =>
                      setSelectedVehicleType(e.target.value || null)
                    }
                  >
                    <option value="">Any Vehicle Type</option>
                    {vehicleTypes.map((vt) => (
                      <option key={vt.id} value={vt.id}>
                        {vt.name}
                      </option>
                    ))}
                  </VehicleTypeSelect>
                </MissionNameInputContent>
              </MissionNameInputWrapper>
            </MissionNameInputContainer>
            <MissionFormContainer>
              <MissionForm
                missionName={missionName}
                onMissionNameChange={setMissionName}
                readOnly={isEditMode}
              >
                <MissionStops
                  missionStops={missionStops}
                  originalMissionStops={originalMissionStops}
                  stops={stops}
                  availableActions={vdaActionDefinitions}
                  availableTriggerTypes={availableTriggerTypes}
                  onAddStep={handleAddStep}
                  onAddAction={handleAddAction}
                  onStationSelect={handleStationSelect}
                  onActionSelect={handleActionSelect}
                  onTriggerTypeSelect={handleTriggerTypeSelect}
                  onDurationChange={handleDurationChange}
                  onRemoveAction={handleRemoveAction}
                  onRemoveStep={handleRemoveStep}
                  onUnhitchChange={handleUnhitchChange}
                  onReorderSteps={handleReorderSteps}
                  onReorderActions={handleReorderActions}
                  onValidationError={handleValidationError}
                  onClearValidationError={clearValidationError}
                  listBottomRef={stepsListBottomRef}
                />
              </MissionForm>

              <div ref={validationErrorsRef}>
                {validationErrors.map((error, index) => (
                  <ValidationError key={index} message={error} />
                ))}
              </div>
            </MissionFormContainer>
          </MissionModalBody>

          {/* Mission Deploy Option - Only show in create mode */}
          {!isEditMode && (
            <MissionDeployOption
              onDeployToggle={handleDeployToggle}
              isDeployEnabled={isDeployEnabled}
            />
          )}

          {/* Mission Deploy - Only show when deploy is enabled and not in edit mode */}
          {isDeployEnabled && !isEditMode && (
            <MissionDeploy
              handleVehiclePick={(vehicleId, selectedLoopsValue) => {
                setSelectedVehicleForDeploy({
                  id: vehicleId,
                  isFleet: false,
                  cycles: Number(selectedLoopsValue),
                });
              }}
              handleFleetPick={(currentFleetId, selectedLoopsValue) => {
                // Guard against empty fleetId (e.g. when "Assign to first available" is unchecked)
                if (!currentFleetId) {
                  setSelectedVehicleForDeploy(null);
                  return;
                }
                setSelectedVehicleForDeploy({
                  id: currentFleetId,
                  isFleet: true,
                  cycles: Number(selectedLoopsValue),
                });
              }}
              cancelAction={() => {
                handleCloseModal();
              }}
              currentMissionDataStops={missionStops.map((stop) => stop.name)}
              vehicleName=""
              vehicleId={null}
              currentVehicles={currentVehicles}
              cycles={cycles}
              onCyclesChange={handleCyclesChange}
              resetTrigger={resetTrigger}
              vehicleType={selectedVehicleType}
              fleetId={currentFleetId}
            />
          )}

          {/* Version Selector - Show when deploy is enabled and mission ID exists (for re-deploy scenarios) */}
          {isDeployEnabled && editData?.id && (
            <VersionSelector
              missionId={editData.id}
              onSelect={setSelectedVersionId}
              disabled={false}
            />
          )}

          <MissionModalFooter>
            <FooterButtonsContainer>
              <CancelMissionButton onClick={handleCloseModal}>
                <ButtonTextContainer>
                  <CancelButtonText>Cancel</CancelButtonText>
                </ButtonTextContainer>
              </CancelMissionButton>
              <CreateMissionButton
                onClick={handleSubmit}
                disabled={!hasChanges || isSaving}
              >
                <ButtonTextContainer>
                  <CreateButtonText>{submitButtonText}</CreateButtonText>
                </ButtonTextContainer>
              </CreateMissionButton>
            </FooterButtonsContainer>
          </MissionModalFooter>
        </MissionModalSidebar>
      </ModalOverlay>
    );
  }
);
